import React, { useState } from 'react';
import { format, addDays } from 'date-fns';
import { Calendar as CalendarIcon, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { TurfCalendar } from './TurfCalendar';

/**
 * Custom date picker — Turf-Green themed, brand-consistent.
 * Replaces native <input type="date">.
 *
 * Props:
 *  - value: 'yyyy-MM-dd' string (or '')
 *  - onChange: (string 'yyyy-MM-dd') => void
 *  - placeholder: string (default "Pick a date")
 *  - minDate, maxDate: Date (default today, today+30)
 *  - testId: data-testid for trigger button
 *  - showWeekendBadge: bool — show "Weekend" pill when applicable
 *  - className: extra class for trigger
 */
export function TurfDatePicker({
  value,
  onChange,
  placeholder = 'Pick a date',
  minDate,
  maxDate,
  testId = 'turf-date-picker',
  showWeekendBadge = false,
  className = '',
  size = 'default', // 'default' | 'compact'
}) {
  const [open, setOpen] = useState(false);
  const selectedDate = value ? new Date(value + 'T00:00:00') : null;
  const isWeekend = selectedDate ? [0, 6].includes(selectedDate.getDay()) : false;

  const min = minDate || new Date();
  const max = maxDate || addDays(new Date(), 30);

  const handleSelect = (d) => {
    onChange(format(d, 'yyyy-MM-dd'));
    setOpen(false);
  };

  const triggerCls = size === 'compact'
    ? 'flex items-center gap-2 h-10 px-3 bg-[#171717] border border-[#1A1A1A] hover:border-[#262626] rounded-lg text-[13px] transition-colors text-left'
    : 'flex items-center gap-3 px-4 py-3 bg-[#171717] border border-[#1A1A1A] hover:border-[#262626] rounded-lg text-sm transition-colors text-left w-full';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className={`${triggerCls} ${className}`}
        >
          <CalendarIcon className="h-4 w-4 text-[#16A34A] flex-shrink-0" />
          <span className={`flex-1 min-w-0 truncate whitespace-nowrap ${value ? 'text-white font-medium' : 'text-neutral-500'}`}>
            {value ? format(selectedDate, 'EEE, dd MMM yyyy') : placeholder}
          </span>
          {showWeekendBadge && isWeekend && (
            <span className="text-[9px] bg-[#16A34A]/10 text-[#16A34A] border border-[#16A34A]/20 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">Weekend</span>
          )}
          <ChevronDown className={`h-3.5 w-3.5 text-neutral-500 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0 bg-transparent border-0 shadow-none"
        align="start"
        sideOffset={6}
      >
        <TurfCalendar
          selected={selectedDate}
          onSelect={handleSelect}
          minDate={min}
          maxDate={max}
        />
      </PopoverContent>
    </Popover>
  );
}

export default TurfDatePicker;
